import React, { useEffect, useState } from 'react';
import { VttItem, vttToJson } from '@osmosis/shared/video';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { P } from '@osmosis/tailwind-ui/components/Text';

export type SubtitlesProps = {
  selectedSubtitle: {
    file: string;
  };
  currentTime: number;
  isSeeking?: boolean;
  containerStyle?: string;
  textStyle?: string;
};

const getSubtitle = async (file: string): Promise<string> => {
  const response = await fetch(file);
  if (!response.ok) throw new Error('Failed to fetch subtitles');
  return await response.text();
};

/**
 * .
 */
const Subtitles = ({
  selectedSubtitle,
  currentTime,
  isSeeking,
  containerStyle = '',
  textStyle = '',
}: SubtitlesProps): React.ReactElement => {
  const [subtitles, setSubtitles] = useState<
    null | { start: number; end: number; part: string }[]
  >(null);

  const [text, setText] = useState('');

  useEffect(() => {
    const asyncGetSubtitles = async () => {
      if (!selectedSubtitle.file) {
        setSubtitles(null);
        setText('');
        return;
      }
      const subtitleType =
        selectedSubtitle.file.split('.')[
          selectedSubtitle.file.split('.').length - 1
        ];

      const openedSubtitle = await getSubtitle(selectedSubtitle.file);

      if (subtitleType === 'vtt') {
        const vtt = vttToJson(openedSubtitle);
        const parsedSubtitle = vtt.items.map(
          ({ startTs, endTs, part }: VttItem) => ({
            start: startTs,
            end: endTs,
            part,
          }),
        );
        setSubtitles(parsedSubtitle);
      }
    };
    void asyncGetSubtitles();
  }, [selectedSubtitle.file]);

  useEffect(() => {
    if (subtitles) {
      const videoTime = Math.floor(currentTime);
      const activeSubtitle = subtitles.find(
        ({ start, end }) => videoTime >= start && videoTime < end,
      );
      setText(activeSubtitle?.part.trim() || '');
    }
  }, [currentTime, subtitles]);

  useEffect(() => {
    setText('');
  }, [isSeeking]);

  return (
    <Flex className={`justify-center absolute bottom-20 ${containerStyle}`}>
      {text.length > 0 ? (
        <Flex
          data-testid="video-subtitles-container"
          className="bg-[rgba(0,0,0,.7)] justify-center rounded-[5px] text-center max-w-full md:max-w-1/2"
        >
          <P
            className={`px-1 py-2 mx-2 !text-white self-center text-shadow-[0_0_#000] ${textStyle}`}
          >
            {text}
          </P>
        </Flex>
      ) : null}
    </Flex>
  );
};

export default Subtitles;
